import classNames from 'clsx';
import React, { type ReactElement, memo, useMemo } from 'react';
import { isFamilySubsInTariff, tariffBuyCustomFeatures, welcomeDefaultTariff } from 'reactApp/appHelpers/featuresHelpers';
import { isFeatureAiLetterPromo, trial256gbAiLetterPromo } from 'reactApp/appHelpers/featuresHelpers/features/aiLetterStylePromo';
import { enabledExpensivePlanTrial } from 'reactApp/appHelpers/featuresHelpers/features/forceTrial';
import { vigboTariffs } from 'reactApp/appHelpers/featuresHelpers/features/vigbo';
import { getQueryParams } from 'reactApp/appHelpers/settingsHelpers';
import type { Product } from 'reactApp/types/Billing';

import styles from './FastCheckoutSidebar.css';
import type { FastCheckoutSidebarQueryParams } from './FastCheckoutSidebar.types';

interface FeaturesListProps {
    customList?: string[];
    product: Product;
    noBorder?: boolean;
    decreaseFontScale?: boolean;
    isExpandedList?: boolean;
}

export const FeaturesList = memo(
    ({ customList, product, noBorder, decreaseFontScale, isExpandedList }: FeaturesListProps): ReactElement => {
        const {
            cancelSending: cancelSendingFromQuery,
            freeSpace: freeSpaceFromQuery,
            features: featuresFromQuery,
        }: FastCheckoutSidebarQueryParams = getQueryParams();

        const cancelSending = cancelSendingFromQuery === 'true';
        const freeSpace = freeSpaceFromQuery === 'true';

        const queryFeatures = useMemo(() => (featuresFromQuery && tariffBuyCustomFeatures[featuresFromQuery]) || [], [featuresFromQuery]);

        const list = useMemo(() => {
            if (customList?.length) {
                return customList;
            }

            // номер списка для семейного тарифа в конфиге фич
            const familyFeatures = tariffBuyCustomFeatures['8'];
            if (product.isFamily && familyFeatures?.length) {
                return familyFeatures;
            }
            // номер списка для vigbo тарифа в конфиге фич
            const vigboFeatures = tariffBuyCustomFeatures['11'];
            if (vigboTariffs.includes(product.id) && vigboFeatures?.length) {
                return vigboFeatures;
            }
            if (queryFeatures?.length) {
                return queryFeatures;
            }

            const cancelSendingText = 'Отмена отправки писем';
            const additionalFeatures = cancelSending ? [cancelSendingText] : [];

            const familyText = 'Добавление ещё\u00A03\u00A0участников';
            const autodeleteTextNew = 'Автоудаление папок в\u00A0выбранное время';
            const autodeleteText = isExpandedList ? autodeleteTextNew : 'Автоудаление папок';
            const familyShareText = 'Делитесь местом с близкими';

            const base = [
                'Без рекламы в\u00A0Почте\u00A0и\u00A0Облаке',
                'Загрузка файлов до 100 ГБ в\u00A0Облако',
                'Отправка файлов до 100 ГБ в\u00A0письмах',
            ];

            const expandedBase = [
                'Без рекламы в\u00A0Почте\u00A0и\u00A0Облаке',
                'Загрузка файлов до 100 ГБ в\u00A0Облако',
                'Отправка вложений до 100 ГБ в\u00A0письмах',
                'Добавление ещё\u00A03\u00A0участников',
                'Пароли для совместных папок в\u00A0веб-версии',
                'Уведомления о прочтении ваших писем',
            ];

            const baseFeatures = isExpandedList ? expandedBase : base;

            // tempexp_17086-start
            if (freeSpace || welcomeDefaultTariff?.tariff === product?.id) {
                return [...baseFeatures, familyShareText, 'Свободная память в телефоне c автозагрузкой'];
            }
            // tempexp_17086-end
            if (isFeatureAiLetterPromo && trial256gbAiLetterPromo === product?.id) {
                return ['Генерация и изменение стиля письма', ...baseFeatures];
            }

            // tempexp_17086-start
            if (freeSpace || welcomeDefaultTariff?.tariff === product?.id) {
                return [...baseFeatures, familyShareText, 'Свободная память в телефоне c автозагрузкой'];
            }
            // tempexp_17086-end

            if (isFamilySubsInTariff && !enabledExpensivePlanTrial) {
                return [...baseFeatures, familyShareText, ...additionalFeatures, autodeleteText];
            }

            if (enabledExpensivePlanTrial && product.hasTrial) {
                return [...baseFeatures, ...additionalFeatures, familyText, autodeleteTextNew];
            }

            return [...baseFeatures, ...additionalFeatures, autodeleteText, 'Приоритетная поддержка'];
        }, [customList, product, cancelSending, freeSpace, queryFeatures]);

        return (
            <ol
                className={classNames(styles.features, {
                    [styles.features_noBorder]: noBorder,
                    [styles.features_decreaseFontScale]: decreaseFontScale,
                })}
            >
                {list.map((text, index) => (
                    <li key={index}>{text}</li>
                ))}
            </ol>
        );
    }
);

FeaturesList.displayName = 'FeaturesList';
